import { BaseRepository } from './base.repository';

export interface EstadoItem {
  uf: string;
  nome: string;
}

export interface CidadeItem {
  cidade: string;
}

export interface BairroItem {
  nome: string;
}

export interface ClinicaItem {
  id: number;
  nome: string;
  cidade?: string;
  estado?: string;
  is_propria?: boolean;
  latitude?: string;
  longitude?: string;
  geolocalizacao_atualizada_em?: string | Date | null;
  agendamento_previo?: boolean;
  possui_integracao?: boolean;
}

export interface ExameClinicoItem {
  id: number;
  nome: string;
  preco?: number;
}

/**
 * Repository para operações relacionadas a clínicas
 */
export class ClinicasRepository extends BaseRepository {
  /**
   * Lista estados disponíveis (baseado nas clínicas)
   */
  async getEstados(): Promise<EstadoItem[]> {
    return this.get<EstadoItem[]>('clinicas/estados');
  }

  /**
   * Lista cidades por estado
   */
  async getCidadesByEstado(estado: string): Promise<CidadeItem[]> {
    return this.get<CidadeItem[]>('clinicas/cidades', {
      params: { estado },
    });
  }

  /**
   * Lista bairros por cidade e estado
   */
  async getBairrosByCidadeEstado(
    estado: string,
    cidade: string
  ): Promise<BairroItem[]> {
    return this.get<BairroItem[]>('clinicas/bairros', {
      params: { estado, cidade },
    });
  }

  /**
   * Lista clínicas com filtros de localização
   */
  async getClinicasFiltradas(filters: {
    estado?: string;
    cidade?: string;
    bairro?: string;
    limit?: number;
    online?: boolean;
    lat?: number;
    lng?: number;
  } = {}): Promise<ClinicaItem[]> {
    const params: any = {};
    if (filters.estado) params.estado = filters.estado;
    if (filters.cidade) params.cidade = filters.cidade;
    if (filters.bairro) params.bairro = filters.bairro;
    if (filters.limit) params.limit = filters.limit;
    if (filters.online !== undefined) params.online = filters.online;
    if (filters.lat !== undefined) params.lat = filters.lat;
    if (filters.lng !== undefined) params.lng = filters.lng;

    return this.get<ClinicaItem[]>('clinicas', { params });
  }

  /**
   * Busca uma clínica específica por ID
   */
  async getById(id: number): Promise<ClinicaItem> {
    return this.get<ClinicaItem>(`clinicas/${id}`);
  }

  /**
   * Busca as próximas datas disponíveis para agendamento em uma clínica
   */
  async getProximasDatasDisponiveis(clinicaId: number, online?: boolean): Promise<string[]> {
    const params: any = { clinicaId };
    if (online !== undefined) {
      params.online = online;
    }
    return this.get<string[]>('clinicas/datas-disponiveis', {
      params,
    });
  }

  /**
   * Busca todos os exames clínicos disponíveis em uma clínica
   */
  async getExamesClinicosClinica(clinicaId: number): Promise<ExameClinicoItem[]> {
    return this.get<ExameClinicoItem[]>('clinicas/exames-clinicos', {
      params: { clinicaId },
    });
  }

  async getHasOnline(): Promise<{ hasOnline: boolean }> {
    return this.get<{ hasOnline: boolean }>('clinicas/has-online');
  }

  /**
   * Atualiza a localização geográfica de uma clínica (Client-Side Geocoding offload)
   */
  async updateGeolocalizacao(id: number, coords: { lat?: number; lng?: number }): Promise<any> {
    return this.patch(`clinicas/${id}/geolocalizacao`, coords);
  }
}

